"use client"

import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import {
  Search,
  MapPin,
  Bed,
  Bath,
  Maximize2,
  ChevronDown,
  ArrowRight,
  Building2,
  Key,
  TrendingUp,
  Shield,
  Loader2
} from "lucide-react"
import { Property, formatPrice, formatDisplayLocation } from "@/lib/supabase"
import { HeroSearchBar } from "@/components/homepage/hero-search-bar"
import { GetAppSection } from "@/components/pwa/get-app-section"

interface Neighborhood {
  name: string
  location: string
  priceRange: string
  properties: number
  area: string
  image?: string
}

// Static luxury neighborhoods data (shown when database is empty)
const STATIC_NEIGHBORHOODS: Neighborhood[] = [
  {
    name: "Forbes Park",
    location: "Makati City",
    priceRange: "₱150M - ₱500M+",
    properties: 45,
    area: "Makati",
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80"
  },
  {
    name: "Bonifacio Global City",
    location: "Taguig City",
    priceRange: "₱60M - ₱300M+",
    properties: 128,
    area: "Taguig",
    image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80"
  },
  {
    name: "Rockwell Center",
    location: "Makati City",
    priceRange: "₱80M - ₱300M+",
    properties: 67,
    area: "Makati",
    image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
  },
  {
    name: "Ayala Alabang",
    location: "Muntinlupa City",
    priceRange: "₱100M - ₱350M+",
    properties: 89,
    area: "Muntinlupa",
    image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80"
  },
  {
    name: "Dasmarinas Village",
    location: "Makati City",
    priceRange: "₱120M - ₱400M+",
    properties: 34,
    area: "Makati",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"
  },
  {
    name: "McKinley Hill",
    location: "Taguig City",
    priceRange: "₱50M - ₱200M+",
    properties: 76,
    area: "Taguig",
    image: "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=800&q=80"
  }
]

// Static featured properties data (shown when database is empty)
const STATIC_FEATURED_PROPERTIES = [
  {
    id: "1",
    title: "Luxurious Penthouse at One Roxas Triangle",
    slug: "luxurious-penthouse-one-roxas-triangle",
    description: "Stunning penthouse with panoramic city views",
    property_type: "penthouse",
    listing_type: "For Sale",
    status: "active",
    price: 350000000,
    currency: "PHP",
    address: "One Roxas Triangle",
    city: "Makati City",
    state: "Metro Manila",
    bedrooms: 4,
    bathrooms: 5,
    floor_area: 450,
    lot_area: null,
    parking: 4,
    furnishing: "Fully Furnished",
    featured_image_url: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80",
    images_urls: ["https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80"],
    project_name: "One Roxas Triangle",
    is_featured: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: "2",
    title: "Modern Villa in Ayala Alabang",
    slug: "modern-villa-ayala-alabang",
    description: "Contemporary design with resort-style amenities",
    property_type: "villa",
    listing_type: "For Sale",
    status: "active",
    price: 280000000,
    currency: "PHP",
    address: "Ayala Alabang Village",
    city: "Muntinlupa City",
    state: "Metro Manila",
    bedrooms: 6,
    bathrooms: 7,
    floor_area: 850,
    lot_area: 1200,
    parking: 6,
    furnishing: "Semi-Furnished",
    featured_image_url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80",
    images_urls: ["https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80"],
    project_name: null,
    is_featured: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: "3",
    title: "BGC Luxury Condo at Grand Hyatt Residences",
    slug: "bgc-luxury-condo-grand-hyatt",
    description: "Premium living in the heart of BGC",
    property_type: "condo",
    listing_type: "For Sale",
    status: "active",
    price: 95000000,
    currency: "PHP",
    address: "Grand Hyatt Manila Residences",
    city: "Taguig City",
    state: "Metro Manila",
    bedrooms: 3,
    bathrooms: 4,
    floor_area: 280,
    lot_area: null,
    parking: 3,
    furnishing: "Fully Furnished",
    featured_image_url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80",
    images_urls: ["https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80"],
    project_name: "Grand Hyatt Manila Residences",
    is_featured: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: "4",
    title: "Forbes Park Estate with Garden",
    slug: "forbes-park-estate-garden",
    description: "Classic elegance in Manila's most prestigious address",
    property_type: "house_and_lot",
    listing_type: "For Sale",
    status: "active",
    price: 450000000,
    currency: "PHP",
    address: "Forbes Park",
    city: "Makati City",
    state: "Metro Manila",
    bedrooms: 7,
    bathrooms: 8,
    floor_area: 1200,
    lot_area: 2000,
    parking: 8,
    furnishing: "Semi-Furnished",
    featured_image_url: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80",
    images_urls: ["https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80"],
    project_name: null,
    is_featured: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
]

export default function HomePage() {
  const router = useRouter()
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([])
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([])
  const [homeStats, setHomeStats] = useState<{ active: number; portfolioValue: number; cities: number } | null>(null)
  const [loading, setLoading] = useState(true)

  // Search form state
  const [searchTab, setSearchTab] = useState<"buy" | "rent">("buy")
  const [searchPropertyType, setSearchPropertyType] = useState("")
  const [searchCity, setSearchCity] = useState("")
  const [searchPriceRange, setSearchPriceRange] = useState("")
  const [showAllRFO, setShowAllRFO] = useState(false)

  // Insight tags for featured properties
  const insightTags = [
    "Best-priced in building",
    "Owner motivated",
    "Rare corner unit",
    "Exclusive listing",
    "Below market value",
    "Prime location",
    "Recently renovated",
    "Investment opportunity"
  ]

  const getInsightTag = (index: number) => {
    return insightTags[index % insightTags.length]
  }

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch featured properties and neighborhoods in parallel
        // Show only premium RESIDENTIAL properties (₱50M+) - condos and houses only
        // Fetch 50 random properties to ensure variety across page loads
        const [propertiesRes, neighborhoodsRes, statsRes] = await Promise.all([
          fetch(`/api/properties?min_price=50000000&property_type=condo,house_and_lot,townhouse&limit=50&random=true&has_images=true`),
          fetch('/api/neighborhoods'),
          fetch('/api/properties/stats')
        ])

        const propertiesData = await propertiesRes.json()
        const neighborhoodsData = await neighborhoodsRes.json()
        const statsData = await statsRes.json()
        if (statsData?.data) {
          setHomeStats({
            active: statsData.data.active || 0,
            portfolioValue: statsData.data.portfolioValue || 0,
            cities: statsData.data.cities || 0,
          })
        }

        // Use static data if database returns empty
        // API returns {success: true, data: {properties: []}}
        const dbProperties = propertiesData.data?.properties || propertiesData.properties || []
        const dbNeighborhoods = (neighborhoodsData.neighborhoods || []).slice(0, 6)

        // Select 4 properties from the fetched random premium properties
        // No need to shuffle again since API returns random results
        const selectedProperties = dbProperties.slice(0, 4)

        setFeaturedProperties(selectedProperties.length > 0 ? selectedProperties : STATIC_FEATURED_PROPERTIES as Property[])
        setNeighborhoods(dbNeighborhoods.length > 0 ? dbNeighborhoods : STATIC_NEIGHBORHOODS)
      } catch (error) {
        console.error('Error fetching data:', error)
        // Fallback to static data on error
        setFeaturedProperties(STATIC_FEATURED_PROPERTIES as Property[])
        setNeighborhoods(STATIC_NEIGHBORHOODS)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleSearch = () => {
    const params = new URLSearchParams()

    // Add listing type based on tab
    params.append('listing_type', searchTab === 'buy' ? 'For Sale' : 'For Rent')

    if (searchPropertyType) {
      params.append('property_type', searchPropertyType)
    }
    if (searchCity) {
      params.append('city', searchCity)
    }
    if (searchPriceRange) {
      // Different price ranges for Buy vs Rent
      if (searchTab === 'buy') {
        switch (searchPriceRange) {
          case '0-50':
            params.append('maxPrice', '50000000')
            break
          case '50-100':
            params.append('minPrice', '50000000')
            params.append('maxPrice', '100000000')
            break
          case '100-200':
            params.append('minPrice', '100000000')
            params.append('maxPrice', '200000000')
            break
          case '200-500':
            params.append('minPrice', '200000000')
            params.append('maxPrice', '500000000')
            break
          case '500+':
            params.append('minPrice', '500000000')
            break
        }
      } else {
        // Rent price ranges (monthly)
        switch (searchPriceRange) {
          case '0-50k':
            params.append('maxPrice', '50000')
            break
          case '50k-100k':
            params.append('minPrice', '50000')
            params.append('maxPrice', '100000')
            break
          case '100k-200k':
            params.append('minPrice', '100000')
            params.append('maxPrice', '200000')
            break
          case '200k-500k':
            params.append('minPrice', '200000')
            params.append('maxPrice', '500000')
            break
          case '500k+':
            params.append('minPrice', '500000')
            break
        }
      }
    }

    const queryString = params.toString()
    router.push(`/properties${queryString ? `?${queryString}` : ''}`)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Integrated Search - Mobile Optimized */}
      <section className="relative min-h-[100svh] flex items-center justify-center pt-16 md:pt-20">
        {/* Background */}
        <div className="absolute inset-0 bg-[#0a0a0a]">
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '100px 100px'
          }} />
        </div>

        <div className="container relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12 md:py-20">
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6 md:space-y-8">
            {/* Premium Badge - Mobile Responsive */}
            <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2 sm:py-2.5 bg-white/[0.03] backdrop-blur-sm border border-white/10">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-[#d60500]" />
              <span className="text-white/70 text-[10px] sm:text-[11px] md:text-[13px] font-light tracking-[0.12em] sm:tracking-[0.15em] uppercase">PRC Licensed No. 0033251</span>
            </div>

            {/* Main Heading - Mobile Optimized Typography */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[80px] font-extralight text-white leading-[1.15] sm:leading-[1.1] tracking-[-0.01em] sm:tracking-[-0.02em] px-2 sm:px-0">
              Extraordinary Living,
              <span className="block font-medium text-[#d60500] mt-1 sm:mt-0">
                Carefully Curated
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed px-4 sm:px-6 md:px-0">
              Private access to the Philippines&apos; most prestigious homes and developments
            </p>

            {/* Enhanced Search Bar with Auto-Suggestions */}
            <div className="mt-8 sm:mt-12 md:mt-16">
              <HeroSearchBar />
            </div>

            {/* Quick Stats - Mobile Optimized Grid */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-6 sm:gap-8 md:gap-12 lg:gap-20 mt-10 sm:mt-16 md:mt-20 pt-6 sm:pt-8 md:pt-10 border-t border-white/10 max-w-4xl mx-auto">
              <div className="text-center px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-white">{homeStats ? homeStats.active.toLocaleString() : '4,326'}<span className="text-[#d60500]">+</span></div>
                <div className="text-gray-500 text-[9px] sm:text-[10px] md:text-[11px] mt-1.5 sm:mt-2 tracking-[0.15em] sm:tracking-[0.2em] uppercase">Properties</div>
              </div>
              <div className="text-center px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-white">₱{homeStats ? Math.round(homeStats.portfolioValue / 1e9) + 'B' : '280B'}<span className="text-[#d60500]">+</span></div>
                <div className="text-gray-500 text-[9px] sm:text-[10px] md:text-[11px] mt-1.5 sm:mt-2 tracking-[0.15em] sm:tracking-[0.2em] uppercase">Portfolio Value</div>
              </div>
              <div className="text-center px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-white">18<span className="text-[#d60500]">+</span></div>
                <div className="text-gray-500 text-[9px] sm:text-[10px] md:text-[11px] mt-1.5 sm:mt-2 tracking-[0.15em] sm:tracking-[0.2em] uppercase">Years</div>
              </div>
              <div className="text-center px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-white">{homeStats ? homeStats.cities.toLocaleString() : '35'}<span className="text-[#d60500]">+</span></div>
                <div className="text-gray-500 text-[9px] sm:text-[10px] md:text-[11px] mt-1.5 sm:mt-2 tracking-[0.15em] sm:tracking-[0.2em] uppercase">Cities</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <div className="w-[1px] h-16 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
        </div>
      </section>

      {/* Why Clients Choose Spire - Trust Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white border-t border-gray-100">
        <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-[#d60500] text-[11px] font-medium tracking-[0.3em] uppercase">Our Advantage</span>
            <h2 className="text-3xl md:text-4xl font-extralight mt-4 tracking-[-0.02em] text-gray-900">
              Why Clients Choose Spire
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {/* PRC Licensed */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#d60500]/5 rounded-lg mb-4">
                <Shield className="w-6 h-6 text-[#d60500]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                PRC-Licensed Brokerage
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                18+ years of professional experience with full regulatory compliance. License No. 0033251.
              </p>
            </div>

            {/* Direct Access */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#d60500]/5 rounded-lg mb-4">
                <Building2 className="w-6 h-6 text-[#d60500]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Direct Developer Access
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Exclusive partnerships with premier developers and private sellers across Metro Manila and beyond.
              </p>
            </div>

            {/* Vetted Properties */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#d60500]/5 rounded-lg mb-4">
                <TrendingUp className="w-6 h-6 text-[#d60500]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Curated Portfolio Only
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                No mass-market listings. Every property is vetted for location, value, and long-term appreciation potential.
              </p>
            </div>

            {/* Discretion */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#d60500]/5 rounded-lg mb-4">
                <Key className="w-6 h-6 text-[#d60500]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Discreet Handling
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Confidential service for high-value transactions. Your privacy and security are our priority.
              </p>
            </div>

            {/* End-to-End Advisory */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#d60500]/5 rounded-lg mb-4">
                <Search className="w-6 h-6 text-[#d60500]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Complete Advisory
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Full support from initial search through pricing strategy, negotiation, and due diligence.
              </p>
            </div>

            {/* Market Expertise */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#d60500]/5 rounded-lg mb-4">
                <MapPin className="w-6 h-6 text-[#d60500]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Local Market Expertise
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Deep knowledge of Metro Manila's most prestigious neighborhoods and emerging investment areas.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 md:mt-16 text-center">
            <Link
              href="/consultation"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#d60500] hover:bg-[#b50400] text-white text-sm font-medium tracking-[0.1em] uppercase transition-all duration-300"
            >
              Schedule a Private Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Properties Section - Mobile Optimized */}
      <section className="py-16 sm:py-20 md:py-28 lg:py-32 bg-white">
        <div className="container max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 sm:mb-12 md:mb-16">
            <div>
              <span className="text-[#d60500] text-[10px] sm:text-[11px] font-medium tracking-[0.25em] sm:tracking-[0.3em] uppercase">Handpicked Selection</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extralight mt-3 sm:mt-4 tracking-[-0.02em]">Featured Properties</h2>
              <p className="text-gray-500 text-sm sm:text-base md:text-lg mt-3 sm:mt-4 max-w-xl font-light leading-relaxed">
                Exceptional homes curated by our luxury property specialists
              </p>
            </div>
            <Link href="/properties" className="mt-6 sm:mt-8 md:mt-0 inline-flex items-center gap-2 sm:gap-3 text-[11px] sm:text-[12px] md:text-[13px] font-medium tracking-[0.08em] sm:tracking-[0.1em] text-[#d60500] hover:gap-4 sm:hover:gap-5 transition-all duration-300 uppercase">
              View All
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#d60500]" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {featuredProperties.map((property, index) => (
                <Link
                  key={property.id}
                  href={`/property/${property.slug}`}
                  className="group cursor-pointer active:opacity-90 transition-opacity flex flex-col h-full"
                >
                  {/* Image Container - Mobile Optimized */}
                  <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden bg-gray-100 rounded-sm sm:rounded-none">
                    {property.featured_image_url || (property.images_urls && property.images_urls.length > 0) ? (
                      <Image
                        src={(property.featured_image_url || property.images_urls?.[0] || '').replace('http://185.249.225.210:9000', 'https://storage.housal.com')}
                        alt={property.title}
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        className="object-cover group-hover:scale-105 transition-transform duration-700"
                        priority={index < 2}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 group-hover:scale-105 transition-transform duration-700">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Building2 className="w-16 h-16 text-gray-400" />
                        </div>
                      </div>
                    )}

                    {/* Badges */}
                    <div className="absolute top-6 left-6 flex gap-3">
                      <span className="px-4 py-2 bg-[#d60500] text-white text-[10px] font-medium tracking-[0.15em] uppercase">
                        {property.listing_type === 'for_sale' || property.listing_type === 'For Sale' ? 'For Sale' :
                         property.listing_type === 'for_rent' || property.listing_type === 'For Rent' ? 'For Rent' :
                         property.listing_type}
                      </span>
                      {property.images_urls && property.images_urls.length > 5 && (
                        <span className="px-4 py-2 bg-black/80 backdrop-blur-sm text-white text-[10px] font-medium tracking-[0.15em] uppercase">
                          {property.images_urls.length} Photos
                        </span>
                      )}
                    </div>

                    {/* Insight Tag - Shows on hover */}
                    <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-gray-900 text-[9px] font-medium tracking-[0.12em] uppercase shadow-lg border border-gray-200">
                        {getInsightTag(index)}
                      </span>
                    </div>

                    {/* Price Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                      <div className="text-3xl font-light text-white tracking-tight">
                        {formatPrice(property.price)}
                        {(property.listing_type === 'For Rent' || property.listing_type === 'for_rent') && <span className="text-lg text-white/70">/mo</span>}
                      </div>
                    </div>
                  </div>

                  {/* Content - Luxury Positioning */}
                  <div className="py-6 flex flex-col flex-1">
                    {/* Title: SEO-intent card title */}
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-[#d60500] transition-colors duration-300 mb-2 tracking-[-0.01em] line-clamp-2">
                      {(property as any).card_title || property.projectName || property.project_name || formatDisplayLocation(property)}
                    </h3>

                    {/* City & Property Type */}
                    <div className="flex items-center gap-4 mb-5 text-sm text-gray-600 font-light">
                      {property.city && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {property.city.replace(/ City$/i, '')}
                        </span>
                      )}
                      {property.property_type && (
                        <span className="flex items-center gap-1.5">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          {property.property_type === 'house_and_lot' || property.property_type === 'house_&_lot' ? 'House & Lot' :
                           property.property_type === 'condo' ? 'Condo' :
                           property.property_type === 'townhouse' ? 'Townhouse' :
                           property.property_type === 'lot' ? 'Lot' :
                           property.property_type === 'office_space' ? 'Office Space' :
                           property.property_type === 'commercial' ? 'Commercial' :
                           property.property_type === 'warehouse' ? 'Warehouse' :
                           property.property_type === 'farm' ? 'Farm' :
                           property.property_type === 'resort' ? 'Resort' :
                           property.property_type === 'parking' ? 'Parking' :
                           property.property_type === 'beach' ? 'Beach Lot' :
                           property.property_type === 'land' ? 'Land' :
                           property.property_type}
                        </span>
                      )}
                    </div>

                    {/* Icon Details Row ONLY */}
                    <div className="flex items-center gap-8 text-gray-500 text-sm font-light pt-5 border-t border-gray-100 mb-5">
                      {property.bedrooms !== null && property.bedrooms > 0 && (
                        <span className="flex items-center gap-2">
                          <Bed className="w-4 h-4 text-gray-400" />
                          {property.bedrooms} Beds
                        </span>
                      )}
                      {property.bathrooms !== null && property.bathrooms > 0 && (
                        <span className="flex items-center gap-2">
                          <Bath className="w-4 h-4 text-gray-400" />
                          {property.bathrooms} Baths
                        </span>
                      )}
                      {/* Floor Area - Always show for all types */}
                      {(property.floor_area || property.floorAreaSqm) && (
                        <span className="flex items-center gap-2">
                          <Maximize2 className="w-4 h-4 text-gray-400" />
                          {property.floor_area || property.floorAreaSqm} sqm
                        </span>
                      )}
                      {/* Lot Area - Show for House & Lot properties IN ADDITION to floor area */}
                      {(property.property_type === 'house_and_lot' || property.property_type === 'house_&_lot') && property.lot_area && (
                        <span className="flex items-center gap-2">
                          <Maximize2 className="w-4 h-4 text-gray-400" />
                          {property.lot_area} sqm lot
                        </span>
                      )}
                    </div>

                    {/* CTA Button — always at bottom */}
                    <div className="w-full bg-[#d60500] group-hover:bg-[#b50400] text-white font-semibold py-3 px-4 transition-all flex items-center justify-center gap-2 tracking-wider text-sm uppercase mt-auto">
                      View Details
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Luxury Neighborhoods Section */}
      <section className="py-32 bg-[#fafafa]">
        <div className="container max-w-[1400px] mx-auto px-6 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <span className="text-[#d60500] text-[11px] font-medium tracking-[0.3em] uppercase">Exclusive Locations</span>
              <h2 className="text-4xl md:text-5xl font-extralight mt-4 tracking-[-0.02em]">Luxury Neighborhoods</h2>
              <p className="text-gray-500 text-lg mt-4 max-w-xl font-light">
                Discover Metro Manila&apos;s most prestigious addresses with luxury properties from ₱50M+
              </p>
            </div>
            <Link href="/neighborhoods" className="mt-8 md:mt-0 inline-flex items-center gap-3 text-[13px] font-medium tracking-[0.1em] text-[#d60500] hover:gap-5 transition-all duration-300 uppercase">
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#d60500]" />
            </div>
          ) : neighborhoods.length === 0 ? (
            <div className="text-center py-20 text-gray-500">Loading neighborhoods...</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {neighborhoods.map((neighborhood, index) => (
                <Link
                  key={index}
                  href={`/properties?city=${encodeURIComponent(neighborhood.name)}`}
                  className="group relative overflow-hidden aspect-[4/3] cursor-pointer"
                >
                  {/* Background Image */}
                  {neighborhood.image ? (
                    <Image
                      src={neighborhood.image}
                      alt={neighborhood.name}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-700"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 group-hover:scale-105 transition-transform duration-700" />
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                  {/* Content - Mobile Optimized */}
                  <div className="absolute inset-0 p-4 sm:p-6 md:p-8 flex flex-col justify-end">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-light text-white mb-1 sm:mb-2 tracking-[-0.01em]">
                      {neighborhood.name}
                    </h3>
                    <p className="text-gray-300 text-sm sm:text-base font-light mb-3 sm:mb-4">{neighborhood.location}</p>

                    {/* Property Count with Visible Background */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm mb-3 sm:mb-4 self-start">
                      <Building2 className="w-3 h-3 text-[#d60500]" />
                      <span className="text-[#d60500] text-[10px] sm:text-[11px] font-semibold tracking-[0.15em] uppercase">
                        {neighborhood.properties} Properties
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-white/80 text-xs sm:text-sm font-light bg-black/30 backdrop-blur-sm px-3 py-1">{neighborhood.priceRange}</span>
                      <span className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-white/20 group-hover:bg-[#d60500] group-hover:border-[#d60500] transition-all duration-300">
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Services Section - Mobile Optimized */}
      <section className="py-16 sm:py-20 md:py-28 lg:py-32 bg-[#0a0a0a]">
        <div className="container max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <span className="text-[#ff5247] text-[10px] sm:text-[11px] font-medium tracking-[0.25em] sm:tracking-[0.3em] uppercase">What We Offer</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extralight mt-3 sm:mt-4 tracking-[-0.02em] text-white">Our Services</h2>
            <p className="text-gray-400 text-sm sm:text-base md:text-lg mt-3 sm:mt-4 max-w-2xl mx-auto font-light leading-relaxed px-4 sm:px-0">
              Comprehensive real estate solutions tailored for discerning clients
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            <div className="group p-6 sm:p-8 md:p-10 bg-[#0a0a0a] hover:bg-[#d60500] active:bg-[#d60500] transition-all duration-500 cursor-pointer touch-manipulation">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center border border-white/20 mb-6 sm:mb-8 group-hover:border-white/40 transition-colors duration-300">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-normal mb-2 sm:mb-3 text-white tracking-[-0.01em]">Buy Property</h3>
              <p className="text-gray-400 group-hover:text-white/70 transition-colors duration-300 font-light text-xs sm:text-sm leading-relaxed">
                Find your dream home from our exclusive portfolio of premium properties
              </p>
            </div>

            <div className="group p-6 sm:p-8 md:p-10 bg-[#0a0a0a] hover:bg-[#d60500] active:bg-[#d60500] transition-all duration-500 cursor-pointer touch-manipulation">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center border border-white/20 mb-6 sm:mb-8 group-hover:border-white/40 transition-colors duration-300">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-normal mb-2 sm:mb-3 text-white tracking-[-0.01em]">Sell Property</h3>
              <p className="text-gray-400 group-hover:text-white/70 transition-colors duration-300 font-light text-xs sm:text-sm leading-relaxed">
                List your property with us and connect with qualified buyers
              </p>
            </div>

            <div className="group p-6 sm:p-8 md:p-10 bg-[#0a0a0a] hover:bg-[#d60500] active:bg-[#d60500] transition-all duration-500 cursor-pointer touch-manipulation">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center border border-white/20 mb-6 sm:mb-8 group-hover:border-white/40 transition-colors duration-300">
                <Key className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-normal mb-2 sm:mb-3 text-white tracking-[-0.01em]">Rent Property</h3>
              <p className="text-gray-400 group-hover:text-white/70 transition-colors duration-300 font-light text-xs sm:text-sm leading-relaxed">
                Premium residential and commercial spaces for lease
              </p>
            </div>

            <div className="group p-6 sm:p-8 md:p-10 bg-[#0a0a0a] hover:bg-[#d60500] active:bg-[#d60500] transition-all duration-500 cursor-pointer touch-manipulation">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center border border-white/20 mb-6 sm:mb-8 group-hover:border-white/40 transition-colors duration-300">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-normal mb-2 sm:mb-3 text-white tracking-[-0.01em]">Investment Advisory</h3>
              <p className="text-gray-400 group-hover:text-white/70 transition-colors duration-300 font-light text-xs sm:text-sm leading-relaxed">
                Expert guidance on real estate investments and portfolio growth
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Condos Section */}
      <section className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="container max-w-[1600px] mx-auto px-6 md:px-8">
          <div className="text-center mb-20">
            <span className="text-[#d60500] text-[11px] font-medium tracking-[0.3em] uppercase">Luxury Condos For Sale</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight mt-4 tracking-[-0.02em]">Premium Condominium Projects</h2>
            <p className="text-gray-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
              Discover exclusive units in Metro Manila&apos;s most prestigious developments from award-winning developers
            </p>
          </div>

          {/* Pre-selling Projects - Large Cards with Images */}
          <div className="mb-32">
            <div className="flex items-center gap-6 mb-12">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-gray-200"></div>
              <div className="text-center">
                <h3 className="text-3xl md:text-4xl font-extralight text-gray-900 tracking-[-0.02em]">Selected New Developments</h3>
                <p className="text-sm text-gray-500 mt-2 font-light">Opportunities chosen for long-term value, location, and build quality</p>
              </div>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent via-gray-200 to-gray-200"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[
                { name: "The Estate", developer: "SMDC & Federal Land", location: "Makati City", image: "/images/projects/The Estate Makati.jpeg" },
                { name: "Haraya Residences", developer: "Shang Properties", location: "Pasig City", image: "/images/projects/Haraya Residences.jpeg" },
                { name: "Laurean Residences", developer: "Ayala Land Premier", location: "Makati City", image: "/images/projects/Laurean Residences Makati.png" },
                { name: "Park Central Towers", developer: "Ayala Land Premier", location: "Makati City", image: "/images/projects/Park Central Towers.jpeg" },
                { name: "Eluria", developer: "Arthaland", location: "Makati City", image: "/images/projects/Eluria.png" },
                { name: "Shang Summit", developer: "Shang Properties", location: "Quezon City", image: "/images/projects/Shang Summit.jpeg" },
                { name: "Shang Bauhinia Residences", developer: "Shang Properties", location: "Cebu City", image: "/images/projects/Shang Bauhinia Residences.jpeg" },
                { name: "Edades West", developer: "Rockwell Land", location: "Rockwell Center Makati", image: "/images/projects/Edades West .jpeg" },
              ].map((project, index) => (
                <Link
                  key={index}
                  href={`/properties?project=${encodeURIComponent(project.name)}`}
                  className="group relative overflow-hidden bg-white shadow-sm hover:shadow-2xl transition-all duration-500"
                >
                  {/* Project Image */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    <Image
                      src={project.image}
                      alt={project.name}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-700"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                    />
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

                    {/* Now Selling Badge */}
                    <div className="absolute top-4 right-4">
                      <span className="px-4 py-2 bg-[#d60500] text-white text-[10px] font-medium tracking-[0.15em] uppercase shadow-lg">
                        Now Selling
                      </span>
                    </div>

                    {/* Project Name on Image */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h4 className="text-xl md:text-2xl font-light text-white mb-1 tracking-[-0.01em] group-hover:text-[#d60500] transition-colors duration-300">
                        {project.name}
                      </h4>
                      <div className="flex items-center gap-2 text-white/80 text-xs">
                        <MapPin className="w-3 h-3" />
                        {project.location}
                      </div>
                    </div>
                  </div>

                  {/* Developer Info */}
                  <div className="p-5 bg-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
                        <Building2 className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-light uppercase tracking-wider">Developer</p>
                        <p className="text-sm text-gray-900 font-normal">{project.developer}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* RFO Projects - Elegant Grid */}
          <div>
            <div className="flex items-center gap-6 mb-12">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-gray-200"></div>
              <div className="text-center">
                <h3 className="text-3xl md:text-4xl font-extralight text-gray-900 tracking-[-0.02em]">Immediate Occupancy Residences</h3>
                <p className="text-sm text-gray-500 mt-2 font-light">Fully completed developments ready for your move</p>
              </div>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent via-gray-200 to-gray-200"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {(() => {
                const allProjects = [
                  { name: "Pacific Plaza Towers", image: "/images/projects/pacific plaza towers BGC.jpeg" },
                  { name: "Horizon Homes", image: "/images/projects/Horizon Homes.jpg.avif" },
                  { name: "Two Roxas Triangle", image: "/images/projects/Two Roxas Triangle.jpeg" },
                  { name: "The Seasons Residences", image: "/images/projects/The Seasons .jpeg" },
                  { name: "Aurelia Residences", image: "/images/projects/Aurelia Residences.jpeg" },
                  { name: "Raffles Residences", image: "/images/projects/Raffles Residences.jpeg" },
                  { name: "One Roxas Triangle", image: "/images/projects/One Roxas.jpeg" },
                  { name: "Hyatt Residences", image: "/images/projects/Hyatt.webp" },
                  { name: "Balmori Suites", image: "/images/projects/Balmori Suites Final.jpg" },
                  { name: "Proscenium at Rockwell", image: "/images/projects/proscenium at rockwell.jpeg" },
                  { name: "Trump Tower", image: "/images/projects/Trump Tower.webp" },
                  { name: "Arya Residences", image: "/images/projects/Arya Residences.avif" },
                  { name: "One Shangri-La Place", image: "/images/projects/One Shangri-La Place.jpeg" },
                  { name: "Imperium at Capitol Commons", image: "/images/projects/Imperium-At-Capitol-Commons-scaled.webp" },
                  { name: "One Serendra", image: "/images/projects/One Serendra.jpeg" },
                  { name: "Park Terraces", image: "/images/projects/Park Terraces.webp" },
                  { name: "Viridian at Greenhills", image: "/images/projects/Viridian at Greenhills.webp" },
                  { name: "Aruga Residences", image: "/images/projects/Aruga Residences .jpeg" },
                  { name: "Essensa East Forbes", image: "/images/projects/Essensa East Forbes.jpeg" },
                  { name: "The St. Francis Shangri-La Place", image: "/images/projects/The St. Francis Shangri-La Place .jpeg" },
                  { name: "West Gallery Place", image: "/images/projects/West Gallery Place .jpeg" },
                  { name: "East Gallery Place", image: "/images/projects/East Gallery Place.jpeg" },
                  { name: "The Empress Capitol Commons", image: "/images/projects/The Empress Capitol Commons.jpg" },
                  { name: "The Suites at BGC", image: "/images/projects/the suites bgc.jpeg" },
                  { name: "Shang Residences at Wack Wack", image: "/images/projects/Shang Residences at Wack Wack .jpeg" },
                ]
                const displayedProjects = showAllRFO ? allProjects : allProjects.slice(0, 10)
                return displayedProjects.map((project, index) => (
                <Link
                  key={index}
                  href={`/properties?project=${encodeURIComponent(project.name)}`}
                  className="group relative overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  {/* Compact Image */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                    <Image
                      src={project.image}
                      alt={project.name}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-700"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                    />
                    {/* Subtle Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-50 group-hover:opacity-70 transition-opacity duration-500" />

                    {/* RFO Badge */}
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1.5 bg-green-500 text-white text-[9px] font-medium tracking-[0.15em] uppercase shadow-md">
                        RFO
                      </span>
                    </div>

                    {/* Project Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h4 className="text-base font-normal text-white group-hover:text-[#d60500] transition-colors duration-300 tracking-[-0.01em] line-clamp-2">
                        {project.name}
                      </h4>
                    </div>
                  </div>
                </Link>
                ))
              })()}
            </div>

            {/* View All Button */}
            {!showAllRFO && (
              <div className="mt-10 text-center">
                <button
                  onClick={() => setShowAllRFO(true)}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white border-2 border-gray-300 hover:border-[#d60500] hover:bg-gray-50 text-gray-900 text-sm font-medium tracking-[0.1em] uppercase transition-all duration-300"
                >
                  <Building2 className="w-4 h-4" />
                  Access 60+ Ready-to-Move-In Residences
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Enhanced CTA */}
          <div className="mt-16 sm:mt-20 text-center relative">
            <div className="absolute inset-0 flex items-center justify-center opacity-5">
              <Building2 className="w-64 h-64 sm:w-96 sm:h-96 text-gray-900" />
            </div>
            <div className="relative px-4">
              <p className="text-gray-600 text-sm sm:text-base mb-5 sm:mb-6 font-light">View all available units in luxury condominiums</p>
              <Link
                href="/properties?listing_type=For+Sale&property_type=condo"
                className="inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-10 md:px-12 py-4 sm:py-5 bg-[#d60500] hover:bg-[#b50400] active:bg-[#a00300] text-white text-[11px] sm:text-[12px] md:text-[13px] font-medium tracking-[0.12em] sm:tracking-[0.15em] uppercase transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 touch-manipulation"
              >
                VIEW ALL LUXURY CONDOS
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile Optimized */}
      <section className="py-16 sm:py-20 md:py-28 lg:py-32 bg-white relative overflow-hidden">
        <div className="container max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 relative">
          <div className="max-w-3xl mx-auto text-center">

            {/* Trust Block - Above CTA */}
            <div className="mb-10 md:mb-12 px-4 sm:px-6 md:px-0">
              <div className="inline-flex flex-col gap-2 text-center">
                <p className="text-sm md:text-base font-medium text-gray-900 tracking-wide">
                  Discreet Representation · Verified Listings · Expert Negotiation
                </p>
                <p className="text-xs md:text-sm text-gray-600 font-light leading-relaxed max-w-2xl">
                  Every property is vetted. Every inquiry handled personally.
                </p>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight mb-4 sm:mb-6 tracking-[-0.02em] px-2">
              Ready to Find Your
              <span className="block text-[#d60500] font-normal mt-1">Perfect Property?</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-500 mb-8 sm:mb-10 md:mb-12 font-light leading-relaxed px-4 sm:px-6 md:px-0">
              Let our expert team guide you through the Philippines&apos; most exclusive real estate opportunities
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 px-4 sm:px-0">
              <Button asChild size="lg" className="bg-[#d60500] hover:bg-[#b50400] active:bg-[#a00300] text-white px-8 sm:px-10 h-12 sm:h-14 text-[11px] sm:text-[12px] md:text-[13px] font-medium tracking-[0.08em] sm:tracking-[0.1em] rounded-none transition-all duration-300 touch-manipulation">
                <Link href="/properties">BROWSE PROPERTIES</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-gray-300 hover:bg-gray-50 active:bg-gray-100 px-8 sm:px-10 h-12 sm:h-14 text-[11px] sm:text-[12px] md:text-[13px] font-medium tracking-[0.08em] sm:tracking-[0.1em] rounded-none transition-all duration-300 touch-manipulation">
                <Link href="/contact">CONTACT US</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <GetAppSection />
    </div>
  )
}
